import React from 'react';
import { Link, ScrollRestoration } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { Footer } from './template';
import { showConsentSettings } from './consent';

// Privacy policy. Keep the "third parties" section in step with what
// index.html and src/index.tsx actually load (and with the purposes/vendors
// configured in AdSense -> Privacy & messaging), and bump LAST_UPDATED.
const LAST_UPDATED = '8 Οκτωβρίου 2026';

export function PrivacyPage() {
  return (
    <div className='max-w-3xl mx-auto p-8'>
      <Helmet>
        <title>Πολιτική απορρήτου — zuper</title>
        <meta name="description" content="Ποια δεδομένα συλλέγει το zuper, πώς χρησιμοποιούνται τα cookies και πώς μπορείτε να αλλάξετε τις επιλογές σας." />
        <link rel="canonical" href="https://www.zuper-market.gr/privacy" />
      </Helmet>

      <div className='mx-auto py-4 text-center text-4xl text-lime-600 font-extrabold tracking-wide'>
        <h1><Link to="/">zuper</Link></h1>
      </div>

      <h2 className='text-2xl pt-2 pb-2'>Πολιτική απορρήτου</h2>
      <p className='pb-6 text-sm text-gray-500'>Τελευταία ενημέρωση: {LAST_UPDATED}</p>

      <div className='space-y-6'>
        <section>
          <h3 className='text-xl pb-2'>Ποιοι είμαστε</h3>
          <p>
            Το zuper (www.zuper-market.gr) λειτουργεί από την{' '}
            <a href="https://www.pixelated-noise.com" target="_blank" className='underline'>Pixelated Noise Ltd.</a>,
            η οποία είναι υπεύθυνη επεξεργασίας για τα δεδομένα που περιγράφονται εδώ.
            Για οποιοδήποτε θέμα σχετικά με τα δεδομένα σας, γράψτε μας στο{' '}
            <a href="mailto:feedback@zuper-market.gr" className='underline'>feedback@zuper-market.gr</a>.
          </p>
        </section>

        <section>
          <h3 className='text-xl pb-2'>Τι δεν συλλέγουμε</h3>
          <p>
            Δεν χρειάζεται λογαριασμός για να χρησιμοποιήσετε το zuper και δεν ζητάμε όνομα,
            email ή άλλα στοιχεία επικοινωνίας. Το εικονικό καλάθι αποθηκεύεται μόνο στη
            συσκευή σας (local storage του browser) και δεν αποστέλλεται σε εμάς.
          </p>
        </section>

        <section>
          <h3 className='text-xl pb-2'>Απαραίτητη λειτουργία του site</h3>
          <p>
            Για να λειτουργήσει το site, ο browser σας επικοινωνεί με τους παρόχους που
            το εξυπηρετούν, οι οποίοι, όπως κάθε διακομιστής, λαμβάνουν τεχνικά στοιχεία
            όπως τη διεύθυνση IP σας:
          </p>
          <ul className='list-disc pl-6 pt-2'>
            <li>Cloudflare, για τη φιλοξενία του site.</li>
            <li>Algolia, για την αναζήτηση προϊόντων (λαμβάνει τους όρους αναζήτησής σας).</li>
            <li>Amazon Web Services, όταν πατάτε σύνδεσμο προς το κατάστημα ενός σούπερ μάρκετ.</li>
          </ul>
        </section>

        <section>
          <h3 className='text-xl pb-2'>Cookies και συναίνεση</h3>
          <p>
            Στην πρώτη σας επίσκεψη σας ζητάμε συναίνεση μέσω της πλατφόρμας διαχείρισης
            συναίνεσης της Google. Μέχρι να απαντήσετε, και αν αρνηθείτε, δεν ενεργοποιούνται
            cookies ανάλυσης επισκεψιμότητας ή μάρκετινγκ. Μπορείτε να αλλάξετε ή να
            ανακαλέσετε τη συναίνεσή σας οποιαδήποτε στιγμή από τον σύνδεσμο «Ρυθμίσεις
            cookies» στο κάτω μέρος κάθε σελίδας ή{' '}
            <button type="button" onClick={showConsentSettings} className='underline text-gray-900'>εδώ</button>.
          </p>
        </section>

        <section>
          <h3 className='text-xl pb-2'>Τρίτοι πάροχοι που χρησιμοποιούμε με τη συναίνεσή σας</h3>
          <ul className='list-disc pl-6 space-y-2'>
            <li>
              <strong>Google Analytics</strong> και <strong>Google Tag Manager</strong>:
              στατιστικά επισκεψιμότητας (ποιες σελίδες επισκέπτεστε, πόσο χρόνο μένετε,
              τι συσκευή και browser χρησιμοποιείτε).
            </li>
            <li>
              <strong>PostHog</strong>: ανάλυση χρήσης για τη βελτίωση του site, συμπεριλαμβανομένης
              της καταγραφής των αλληλεπιδράσεών σας με τη σελίδα (κλικ, κύλιση). Δεν
              καταγράφονται στοιχεία που πληκτρολογείτε σε πεδία κωδικών.
            </li>
            <li>
              <strong>Google AdSense</strong> και οι διαφημιστικοί συνεργάτες της Google: προβολή
              διαφημίσεων. Με τη συναίνεσή σας οι διαφημίσεις μπορεί να είναι εξατομικευμένες·
              χωρίς αυτήν εμφανίζονται μη εξατομικευμένες διαφημίσεις. Η πλήρης λίστα
              συνεργατών εμφανίζεται στο παράθυρο συναίνεσης.
            </li>
            <li>
              <strong>Εργαλεία μέτρησης διαφημιστικών καμπανιών</strong>, όπως το LinkedIn
              Insight Tag: μέτρηση της αποτελεσματικότητας των δικών μας διαφημίσεων.
            </li>
          </ul>
          <p className='pt-2'>
            Ορισμένοι από αυτούς τους παρόχους βρίσκονται εκτός Ευρωπαϊκού Οικονομικού
            Χώρου (π.χ. στις ΗΠΑ). Η διαβίβαση γίνεται με τις εγγυήσεις που προβλέπει ο
            ΓΚΠΔ, όπως οι τυποποιημένες συμβατικές ρήτρες της Ευρωπαϊκής Επιτροπής.
          </p>
        </section>

        <section>
          <h3 className='text-xl pb-2'>Τα δικαιώματά σας</h3>
          <p>
            Σύμφωνα με τον Γενικό Κανονισμό Προστασίας Δεδομένων (ΓΚΠΔ), έχετε δικαίωμα
            πρόσβασης, διόρθωσης, διαγραφής, περιορισμού και φορητότητας των δεδομένων σας,
            καθώς και δικαίωμα εναντίωσης και ανάκλησης της συναίνεσής σας. Για να τα
            ασκήσετε, γράψτε μας στο{' '}
            <a href="mailto:feedback@zuper-market.gr" className='underline'>feedback@zuper-market.gr</a>.
            Έχετε επίσης δικαίωμα να υποβάλετε καταγγελία στην{' '}
            <a href="https://www.dpa.gr" target="_blank" className='underline'>Αρχή Προστασίας Δεδομένων Προσωπικού Χαρακτήρα</a>.
          </p>
        </section>
      </div>

      <div className="pt-32">
        <Footer />
      </div>
      <ScrollRestoration />
    </div>
  );
}
